'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { FeatureTriplet } from '@/components/site/feature-triplet'
import { Reveal } from '@/components/site/reveal'
import { SectionHead } from '@/components/site/section-head'
import { SiteSection } from '@/components/site/section'

export function SpeakerSection() {
  const { t } = useTranslation(['web'])
  return (
    <SiteSection divider trackingKey="speakers">
      <Reveal>
        <SectionHead
          eyebrow={t('web:meetingNotesPage.speakers.eyebrow')}
          titleKey="web:meetingNotesPage.speakers.title"
          lead={t('web:meetingNotesPage.speakers.lead')}
        />
      </Reveal>
      <Reveal className="mt-(--gap-xl)">
        <div
          role="img"
          aria-label={t('web:meetingNotesPage.speakers.aria')}
          className="grid grid-cols-[1.15fr_1fr] overflow-hidden rounded-xl border border-border bg-background max-[920px]:grid-cols-1"
        >
          <TranscriptDemo />
          <NotesDemo />
        </div>
      </Reveal>
      <Reveal className="mt-(--gap-xl)">
        <FeatureTriplet
          // value-list: local — i18n key stems of this section's three cards, not a shared value set
          items={['one', 'two', 'three'].map((id) => ({
            title: t(`web:meetingNotesPage.speakers.facts.${id}Title`),
            description: t(`web:meetingNotesPage.speakers.facts.${id}Desc`),
          }))}
        />
      </Reveal>
    </SiteSection>
  )
}
function TranscriptDemo() {
  const { t } = useTranslation(['web'])
  return (
    <div aria-hidden="true" className="min-w-0 p-[clamp(20px,3vw,24px)]">
      <p className="font-mono text-xs text-muted">
        {t('web:meetingNotesPage.speakers.transcript')}
      </p>
      <div className="mt-4 flex gap-2">
        <span className="rounded-full border border-foreground px-3 py-1 text-sm">
          {t('web:meetingNotesPage.speakers.demo.speakerOne')}
        </span>
        <span className="rounded-full border border-border px-3 py-1 text-sm">
          {t('web:meetingNotesPage.speakers.demo.speakerTwo')}
        </span>
      </div>
      <p className="mt-3 text-sm text-muted">{t('web:meetingNotesPage.speakers.renameHint')}</p>
      <div className="mt-4 text-sm leading-[1.6]">
        <TranscriptCue speakerKey="speakerOne" time="00:15" cueKey="cueOne" />
        <TranscriptCue speakerKey="speakerTwo" time="05:10" cueKey="cueTwo" />
        <TranscriptCue speakerKey="speakerOne" time="19:30" cueKey="cueThree" />
      </div>
    </div>
  )
}
function NotesDemo() {
  const { t } = useTranslation(['web'])
  return (
    <div
      aria-hidden="true"
      className="min-w-0 border-l border-border p-[clamp(20px,3vw,24px)] max-[920px]:border-t max-[920px]:border-l-0"
    >
      <p className="font-mono text-xs text-muted">{t('web:meetingNotesPage.speakers.notes')}</p>
      <h3 className="mt-5 font-semibold">{t('web:meetingNotesPage.speakers.confirmed')}</h3>
      <ul className="mt-2 list-disc pl-[1.1em] text-sm leading-[1.65]">
        <li>
          {t('web:meetingNotesPage.speakers.demo.confirmedText')} <Timecode value="12:40" />
        </li>
      </ul>
      <h3 className="mt-5 font-semibold">{t('web:meetingNotesPage.speakers.todo')}</h3>
      <table className="mt-2 w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-2 pr-3">{t('web:meetingNotesPage.speakers.demo.task')}</th>
            <th className="py-2 pr-3">{t('web:meetingNotesPage.speakers.demo.owner')}</th>
            <th className="py-2">{t('web:meetingNotesPage.speakers.demo.due')}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border">
            <td className="py-3 pr-3 align-top">
              {t('web:meetingNotesPage.speakers.demo.taskOne')} <Timecode value="00:15" />
            </td>
            <td className="py-3 pr-3 align-top font-semibold whitespace-nowrap">
              {t('web:meetingNotesPage.speakers.demo.speakerOne')}
            </td>
            <td className="py-3 align-top whitespace-nowrap text-muted">
              {t('web:meetingNotesPage.speakers.demo.dueOne')}
            </td>
          </tr>
          <tr>
            <td className="py-3 pr-3 align-top">
              {t('web:meetingNotesPage.speakers.demo.taskTwo')} <Timecode value="05:10" />
            </td>
            <td className="py-3 pr-3 align-top font-semibold whitespace-nowrap">
              {t('web:meetingNotesPage.speakers.demo.speakerTwo')}
            </td>
            <td className="py-3 align-top whitespace-nowrap text-muted">
              {t('web:meetingNotesPage.speakers.demo.dueTwo')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
function Timecode({ value }: { value: string }) {
  return (
    <span className="ml-1 inline-block border-b border-brand/40 font-mono text-2xs tracking-[0.04em] text-brand align-[1px]">
      {value}
    </span>
  )
}

function TranscriptCue({
  speakerKey,
  time,
  cueKey,
}: {
  speakerKey: string
  time: string
  cueKey: string
}) {
  const { t } = useTranslation(['web'])
  return (
    <p className="grid grid-cols-[5.4em_1fr] gap-x-3 border-t border-line-soft py-3 pl-3">
      <b>{t(`web:meetingNotesPage.speakers.demo.${speakerKey}`)}</b>
      <span className="row-span-2">{t(`web:meetingNotesPage.speakers.demo.${cueKey}`)}</span>
      <span className="font-mono text-xs text-muted">{time}</span>
    </p>
  )
}
